import { asMemberId, asPlannedTrialId, mintEntityId, type ParameterAnswer } from "@repo/domain";
import {
  type DataGridCellSelectionApi,
  type DataGridCellsChangeDetails,
  dataGridFeatures,
} from "@repo/ui/components/reui/data-grid";
import { Button } from "@repo/ui/components/ui/button";
import { CellEditor, type CellEditorCommitVia, type CellEditorValue } from "@repo/ui/components/ui/cell-editor";
import { TableCell } from "@repo/ui/components/ui/table";
import { useTable } from "@tanstack/react-table";
import { XIcon } from "lucide-react";
import { useRef, useState } from "react";
import type { PresentedOrderSetDto, PresentedOrderSetParameterDto } from "@/lib/order-set-presentation";
import {
  TRIAL_GRID_ACTIONS_COLUMN,
  TRIAL_GRID_DRAG_COLUMN,
  TRIAL_GRID_INITIAL_STATE,
  type TrialEditRequest,
  TrialGrid,
} from "@/shared/trials/trial-grid";
import {
  displayTrialAnswer,
  parseTrialClipboard,
  trialEditorAnswer,
  trialEditorProps,
  trialParameterKey,
  trialProblem,
} from "@/shared/trials/trial-values";
import type {
  ProtocolBuilderDomainAnswer,
  ProtocolBuilderParameter,
  ProtocolBuilderPlannedTrial,
} from "./protocol-builder-definition";

const STEP: Partial<Record<CellEditorCommitVia, 1 | -1>> = {
  enter: 1,
  tab: 1,
  "shift-enter": -1,
  "shift-tab": -1,
};

/** The builder's declarations in the charting grid's shape. */
export function plannedTrialFields(parameters: readonly ProtocolBuilderParameter[]): PresentedOrderSetParameterDto[] {
  return parameters.map((parameter) => ({
    ...parameter,
    value:
      parameter.value.kind === "Choice"
        ? {
            ...parameter.value,
            options: parameter.value.options.map((option) => ({ optionId: option.id, label: option.label })),
          }
        : parameter.value,
  }));
}

/**
 * State and wiring for a planned-trials grid: one table row per planned trial, keyed by id, never by position, so
 * editing, removing or duplicating one row cannot move another row's values. Each grid variant composes it with
 * its own columns around the shared parameter cells.
 */
export function usePlannedTrialsGrid({
  parameters,
  trials,
  onChange,
  readOnly,
  reorderable,
  label,
}: {
  parameters: readonly PresentedOrderSetParameterDto[];
  trials: readonly ProtocolBuilderPlannedTrial[];
  onChange: (trials: ProtocolBuilderPlannedTrial[]) => void;
  readOnly: boolean;
  reorderable: boolean;
  label: string;
}) {
  const selection = useRef<DataGridCellSelectionApi>(null);
  const [editing, setEditing] = useState<TrialEditRequest>();
  const [error, setError] = useState<string>();
  const data = trials.map((trial) => ({ id: trial.id, values: trial.values.map(toParameterAnswer) }));
  const columns = [
    ...(readOnly || !reorderable ? [] : [TRIAL_GRID_DRAG_COLUMN]),
    { id: "number", header: "#", enableCellSelection: false },
    ...parameters.map((parameter) => ({
      id: trialParameterKey(parameter),
      header: parameter.label,
      accessorFn: (row: (typeof data)[number]) =>
        row.values.find((answer) => trialParameterKey(answer) === trialParameterKey(parameter)),
      meta: {
        cellEdit: {
          editable: !readOnly,
          format: (value: unknown) => (value ? displayTrialAnswer(value as ParameterAnswer, parameters) : ""),
          parse: (raw: string) => {
            const parsed = parseTrialClipboard(parameter, raw);
            return parsed ? parsed.answer : parsed;
          },
        },
      },
    })),
    TRIAL_GRID_ACTIONS_COLUMN,
  ];
  const table = useTable({
    features: dataGridFeatures,
    data,
    columns,
    getRowId: (row) => row.id,
    manualPagination: true,
    initialState: TRIAL_GRID_INITIAL_STATE,
  });

  function commit(
    trialId: string,
    field: PresentedOrderSetParameterDto,
    key: string,
    edited: CellEditorValue,
    via: CellEditorCommitVia,
  ) {
    const next = trialEditorAnswer(field, edited);
    if (next === false) return false;
    const problem = next ? trialProblem([field], [{ answer: next, source: "entered" }]) : undefined;
    if (problem) {
      setError(problem);
      return false;
    }
    setError(undefined);
    onChange(withAnswer(trials, trialId, key, next));
    setEditing(undefined);
    const step = STEP[via];
    if (step) navigate(trialId, key, step);
    return true;
  }

  /** Tab wraps rows; past the grid's first or last cell, focus stays on the edited cell. */
  function navigate(trialId: string, key: string, step: 1 | -1) {
    const row = trials.findIndex((trial) => trial.id === trialId);
    const column = parameters.findIndex((field) => trialParameterKey(field) === key) + step;
    const wraps = column < 0 || column >= parameters.length;
    const trial = trials[wraps ? row + step : row];
    const field = parameters[wraps ? (step > 0 ? 0 : parameters.length - 1) : column];
    if (trial && field) selection.current?.focusCell(trial.id, trialParameterKey(field));
    else selection.current?.focusCell(trialId, key);
  }

  return {
    table,
    fields: parameters,
    selection,
    editing,
    error,
    readOnly,
    commit,
    cancel(rowId: string, key: string) {
      setEditing(undefined);
      selection.current?.focusCell(rowId, key);
    },
    remove(trialId: string) {
      setEditing(undefined);
      onChange(trials.filter((trial) => trial.id !== trialId));
    },
    rootProps: {
      table,
      parameters,
      label,
      selection,
      onCellEditRequest: readOnly ? undefined : setEditing,
      onReorder:
        readOnly || !reorderable
          ? undefined
          : (from: number, to: number) => {
              setEditing(undefined);
              onChange(moved(trials, from, to));
            },
      onCellsChange: ({ changes, rejected }: DataGridCellsChangeDetails<(typeof data)[number]>) => {
        if (readOnly) return;
        setError(rejected.length ? "Some pasted values were invalid and were not changed." : undefined);
        onChange(
          changes.reduce(
            (next, change) =>
              withAnswer(next, change.rowId, change.columnId, change.value as ParameterAnswer | undefined),
            [...trials],
          ),
        );
      },
    },
  };
}

export type PlannedTrialsGridState = ReturnType<typeof usePlannedTrialsGrid>;
type PlannedTrialGridRow = ReturnType<PlannedTrialsGridState["table"]["getRowModel"]>["rows"][number];

export function PlannedTrialsError({ grid }: { grid: PlannedTrialsGridState }) {
  return grid.error ? (
    <p role="alert" className="text-sm text-destructive">
      {grid.error}
    </p>
  ) : null;
}

/** One planned trial's parameter cells, editable in place unless the grid is read-only. */
export function PlannedTrialCells({
  grid,
  row,
  index,
}: {
  grid: PlannedTrialsGridState;
  row: PlannedTrialGridRow;
  index: number;
}) {
  return grid.fields.map((field) => {
    const key = trialParameterKey(field);
    const answer = row.original.values.find((value) => trialParameterKey(value) === key);
    const editing = grid.editing;
    const active = !grid.readOnly && editing?.rowId === row.id && editing.columnId === key;
    return (
      <TrialGrid.Cell key={key} row={row} column={key} label={`Trial ${index + 1}: ${field.label}`}>
        <TrialGrid.Overlay
          editor={
            active && (
              <CellEditor
                key={`${row.id}:${key}`}
                label={field.label}
                {...trialEditorProps(field, answer)}
                initialText={editing.initialText}
                className="absolute inset-0 z-[35] px-2 py-1"
                onCancel={() => grid.cancel(row.id, key)}
                onCommit={(edited, via) => grid.commit(row.id, field, key, edited, via)}
              />
            )
          }
        >
          <span className={answer ? undefined : "text-muted-foreground"}>
            {answer ? displayTrialAnswer(answer, grid.fields) : "—"}
          </span>
        </TrialGrid.Overlay>
      </TrialGrid.Cell>
    );
  });
}

/** The authoring grid (protocol builder, order sheet): edit, drag to reorder, remove. */
export function PlannedTrialsGrid({
  parameters,
  trials,
  onChange,
  readOnly = false,
  reference = false,
}: {
  parameters: readonly PresentedOrderSetParameterDto[];
  trials: readonly ProtocolBuilderPlannedTrial[];
  onChange: (trials: ProtocolBuilderPlannedTrial[]) => void;
  readOnly?: boolean;
  /** Styled and numbered (P1, P2) like the procedure card's plan, where an order's plan sits beside what was done. */
  reference?: boolean;
}) {
  const grid = usePlannedTrialsGrid({
    parameters,
    trials,
    onChange,
    readOnly,
    reorderable: true,
    label: "Planned trials grid",
  });
  return (
    <div className="w-full min-w-0 max-w-full space-y-3 [contain:inline-size]">
      <PlannedTrialsError grid={grid} />
      <TrialGrid.Root {...grid.rootProps} reference={reference}>
        <TrialGrid.Header>
          <TrialGrid.DragHead />
          <TrialGrid.Head>#</TrialGrid.Head>
          <TrialGrid.ParameterHeads />
          <TrialGrid.ActionsHead label="Remove" />
        </TrialGrid.Header>
        <TrialGrid.Body>
          {grid.table.getRowModel().rows.map((row, index) => {
            const number = reference ? `P${index + 1}` : `${index + 1}`;
            return (
              <TrialGrid.Row
                key={row.id}
                id={row.id}
                index={index}
                data-row-id={row.id}
                data-cell-editing={grid.editing?.rowId === row.id ? "" : undefined}
              >
                <TrialGrid.DragHandle row={row} label={`Reorder planned trial ${number}`} />
                <TableCell>{number}</TableCell>
                <PlannedTrialCells grid={grid} row={row} index={index} />
                <TrialGrid.ActionsCell row={row}>
                  {!readOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="text-muted-foreground hover:text-foreground"
                      aria-label={`Remove planned trial ${number}`}
                      onClick={() => grid.remove(row.id)}
                    >
                      <XIcon />
                    </Button>
                  )}
                </TrialGrid.ActionsCell>
              </TrialGrid.Row>
            );
          })}
        </TrialGrid.Body>
      </TrialGrid.Root>
    </div>
  );
}

export function PlannedTrials({
  parameters,
  trials,
  onChange,
  readOnly = false,
}: {
  parameters: readonly PresentedOrderSetParameterDto[];
  trials: readonly ProtocolBuilderPlannedTrial[];
  onChange: (trials: ProtocolBuilderPlannedTrial[]) => void;
  readOnly?: boolean;
}) {
  return (
    <section className="w-full min-w-0 max-w-full space-y-3 [contain:inline-size]" aria-label="Planned trials">
      <h3 className="text-sm font-medium">
        Planned trials <span className="font-normal text-muted-foreground">{trials.length}</span>
      </h3>
      <PlannedTrialsGrid parameters={parameters} trials={trials} onChange={onChange} readOnly={readOnly} />
      {!readOnly && <PlannedTrialAddActions trials={trials} onChange={onChange} />}
    </section>
  );
}

function PlannedTrialAddActions({
  trials,
  onChange,
  addLabel,
}: {
  trials: readonly ProtocolBuilderPlannedTrial[];
  onChange: (trials: ProtocolBuilderPlannedTrial[]) => void;
  addLabel?: string;
}) {
  const previous = trials.at(-1);
  return (
    <TrialGrid.AddActions
      addLabel={addLabel}
      onAdd={() => onChange([...trials, { id: asPlannedTrialId(mintEntityId()), values: [] }])}
      onDuplicate={
        previous &&
        (() =>
          onChange([...trials, { id: asPlannedTrialId(mintEntityId()), values: structuredClone(previous.values) }]))
      }
    />
  );
}

/** An order's planned trials in the grid; read-only unless it is editable and has somewhere to send edits. */
export function OrderPlannedTrials({
  order,
  editable,
  onChange,
}: {
  order: PresentedOrderSetDto["orders"][number];
  editable: boolean;
  onChange?: (plannedTrials: ProtocolBuilderPlannedTrial[]) => void;
}) {
  const trials = builderPlannedTrials(order.plannedTrials);
  if (!trials.length && !(editable && onChange)) {
    return order.definition.parameters.length ? (
      <p className="text-sm text-muted-foreground">No planned trials.</p>
    ) : null;
  }
  const readOnly = !(editable && onChange);
  const change = (next: ProtocolBuilderPlannedTrial[]) => onChange?.(next);
  return (
    <section
      key={order.orderId}
      className="w-full min-w-0 max-w-full space-y-3 [contain:inline-size]"
      aria-label="Planned trials"
    >
      <TrialGrid.SectionLabel title="Planned" hint="Reference only. What was done is charted on the procedure card." />
      <PlannedTrialsGrid
        parameters={order.definition.parameters}
        trials={trials}
        readOnly={readOnly}
        onChange={change}
        reference
      />
      {!readOnly && <PlannedTrialAddActions trials={trials} onChange={change} addLabel="Add planned trial" />}
    </section>
  );
}

/** An order's planned trials in the builder's editable shape. */
export function builderPlannedTrials(
  planned: PresentedOrderSetDto["orders"][number]["plannedTrials"],
): ProtocolBuilderPlannedTrial[] {
  return (planned ?? []).map((trial) => ({
    id: trial.id,
    values: trial.values.map((answer) => ({
      ...answer,
      value: answer.value.kind === "Choice" ? { ...answer.value, selected: [...answer.value.selected] } : answer.value,
    })),
  }));
}

function moved<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

function withAnswer(
  trials: readonly ProtocolBuilderPlannedTrial[],
  trialId: string,
  column: string,
  answer: ParameterAnswer | undefined,
): ProtocolBuilderPlannedTrial[] {
  return trials.map((trial) =>
    trial.id === trialId
      ? {
          ...trial,
          values: [
            ...trial.values.filter((value) => trialParameterKey(value) !== column),
            ...(answer ? [toBuilderAnswer(answer)] : []),
          ],
        }
      : trial,
  );
}

function toParameterAnswer(answer: ProtocolBuilderDomainAnswer): ParameterAnswer {
  return {
    source: answer.source === "self" ? "self" : asMemberId(answer.source),
    parameterId: asMemberId(answer.parameterId),
    value:
      answer.value.kind === "Choice"
        ? { ...answer.value, selected: answer.value.selected.map(asMemberId) }
        : answer.value,
  };
}

function toBuilderAnswer(answer: ParameterAnswer): ProtocolBuilderDomainAnswer {
  return {
    source: answer.source,
    parameterId: answer.parameterId,
    value: answer.value.kind === "Choice" ? { ...answer.value, selected: [...answer.value.selected] } : answer.value,
  };
}
